import type { QuoteSchema } from '@/services/quotes-schemas';
import type { BusinessRisk } from '@/services/quotes/types';
import { RequiredTooltipProvider } from '@/shared/components/required-field-tooltip';
import { Steps } from '@noorden-insurtech/alba-design-system';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import type { BrokerProductCategory } from '@/services/brokers/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuoteSchemas } from '../../quotes-schemas';
import { useQuoteParams } from '../context/quote-params.context';
import { useQuoteForm } from '../hooks/common/use-quote-form.hook';
import { useProposalDetails } from '../hooks/homeowner/use-proposal-details.hook';
import type { QuoteFormValues } from '../types/quote-form.types';
import { resolveActiveStepCode } from '../utils/resolve-active-step-code.util';
import type { CoverageCardData } from './coverage-card.component';
import { NewQuoteFormSkeleton } from './new-quote-form-skeleton.component';
import {
  StepCalculation,
  StepCommercialization,
  StepCoverages,
  StepGroupLifeCommercialization,
  StepInsuredData,
  StepPlan,
  StepPropertyData,
  StepRisk,
  StepRiskBusinessData,
  StepStipulator,
  SummaryView,
} from './quote-steps';
import { GroupLifeQuoteProvider } from '../context/group-life-quote.context';
import { useGroupLifeStepFilled } from '../hooks/group-life/use-group-life-step-filled.hook';
import { useGroupLifeQuoteDetails } from '../hooks/group-life/use-group-life-quote-details.hook';
import { useQuotationVersionId } from '../hooks/group-life/use-quotation-version-id.hook';
import { isTerminalPortalStatus } from '@/shared/enums/portal-status.enum';
import { useHydrateGroupLifeQuote } from '../hooks/group-life/use-hydrate-quote.hook';
import { QuoteSummary } from './quote-summary.component';

type Props = {
  stepCodeFromSearch: string;
  onLastStepChange?: (isLastStep: boolean) => void;
};

export function NewQuoteForm({ stepCodeFromSearch, onLastStepChange }: Props) {
  const { setParams, id, policyId, ...ctxParams } = useQuoteParams();
  const params = useParams({ strict: false });
  // IMPORTANTE: produto/categoria vêm da URL (search) — fonte síncrona e
  // correta no refresh. O contexto (`useQuoteParams`) é populado por um
  // useEffect do layout e fica VAZIO na janela de hidratação do refresh; se
  // usássemos o contexto aqui, o efeito de normalização de step abaixo
  // navegaria (replace) reescrevendo a URL com category/productCode VAZIOS,
  // derrubando a cotação PME para Residencial. Lendo da URL, isso não ocorre.
  const search = useSearch({ strict: false }) as {
    productId?: string;
    category?: string;
    productCode?: string;
    quoteFlow?: string;
  };
  const productId = search.productId || ctxParams.productId || '';
  const category = (search.category ||
    ctxParams.category ||
    '') as BrokerProductCategory;
  const productCode = search.productCode || ctxParams.productCode || '';
  const quoteFlow: 'new' | 'edit' =
    search.quoteFlow === 'edit' ? 'edit' : 'new';
  /**
   * Cotação REABERTA para consulta: é o `quoteFlow=view` que o
   * `quote-details` manda ao redirecionar o PME para o wizard dedicado.
   *
   * Fica separado do `quoteFlow` acima de propósito — aquele alimenta o
   * layout/contexto e só distingue 'new' de 'edit'. Aqui o que importa é se o
   * corretor está voltando numa cotação que já existe.
   */
  const isViewFlow = search.quoteFlow === 'view';
  const resolvedQuoteId =
    (typeof params.id === 'string' && params.id.trim()) ||
    id?.trim() ||
    policyId?.trim() ||
    '';
  const { data: proposalDetails } = useProposalDetails(resolvedQuoteId);
  const initialBusinessRisks = useMemo<BusinessRisk[]>(
    () => (proposalDetails?.lobs?.[0]?.risks ?? []) as BusinessRisk[],
    [proposalDetails]
  );
  const [coveragesState, setCoveragesState] = useState<CoverageCardData[]>([]);
  const [assistanceChoice, setAssistanceChoice] = useState<string>('140001ESP');
  const [insuredObjectsState, setInsuredObjectsState] = useState<
    Array<{ insuredObjectType: string; riskValue: string }>
  >([]);
  const vrdTotal = insuredObjectsState.reduce((acc, row) => {
    const numeric = parseInt(
      String(row.riskValue || '').replace(/\D/g, ''),
      10
    );
    if (Number.isNaN(numeric)) return acc;
    return acc + numeric / 100;
  }, 0);
  const navigate = useNavigate();
  const { data: schema, isLoading } = useQuoteSchemas(
    category || 'HOME_INSURANCE'
  );

  const steps = schema?.steps ?? [];
  const activeCode = useMemo(
    () => resolveActiveStepCode(steps, stepCodeFromSearch),
    [steps, stepCodeFromSearch]
  );
  const activeIndex = useMemo(
    () => steps.findIndex(s => s.code === activeCode),
    [steps, activeCode]
  );

  const baseSearch = useMemo(
    () => ({
      productId: productId || '',
      category: (category || '') as string,
      productCode: productCode || '',
      quoteFlow,
    }),
    [productId, category, productCode, quoteFlow]
  );

  const goToStepCode = useCallback(
    (nextCode: string, replace = false) => {
      const search = { ...baseSearch, stepCode: nextCode };
      const policyRowId =
        (typeof params.id === 'string' && params.id.trim()) ||
        id?.trim() ||
        policyId?.trim() ||
        '';
      if (policyRowId) {
        navigate({
          to: '/quotes/products/new/$id',
          params: { id: policyRowId },
          search,
          replace,
          viewTransition: false,
        }).catch(() => {});
      } else {
        navigate({
          to: '/quotes/products/new',
          search,
          replace,
          viewTransition: false,
        }).catch(() => {});
      }
    },
    [navigate, baseSearch, params.id, id, policyId]
  );

  useEffect(() => {
    if (!steps.length) return;
    if (activeCode === stepCodeFromSearch) return;
    goToStepCode(activeCode, true);
  }, [steps.length, activeCode, stepCodeFromSearch, goToStepCode]);

  const isLastStep =
    steps.length > 0 && activeIndex >= 0 && activeIndex === steps.length - 1;

  useEffect(() => {
    onLastStepChange?.(isLastStep);
  }, [isLastStep, onLastStepChange]);

  const mainForm = useQuoteForm({
    defaultValues: {
      insuredPerson: {
        name: '',
        hasSocialName: false,
        socialName: '',
        cpf: '',
        phone: '',
        email: '',
        congener: '',
        policyNumber: '',
        bonus: '',
      },
      policy: {
        issuanceType: 'new' as const,
        startDate: '',
        endDate: '',
      },
      property: {
        cep: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
        propertyType: '',
        constructionType: '',
        propertyClassification: '',
        activityType: '',
        insuredObjectType: '',
        riskValue: '',
        isLandmarked: false,
        builtArea: '',
      },
    } as Partial<QuoteFormValues>,
    onSubmit: async () => {
      // TODO: Enviar para o backend
    },
  });

  const hasNavigatedToId = useRef(false);

  const handleNextStep = (policyIdArg?: string, quoteIdArg?: string) => {
    if (!steps.length) return;
    const idx = steps.findIndex(s => s.code === activeCode);
    const safeIdx = idx < 0 ? 0 : idx;
    const nextIdx = Math.min(safeIdx + 1, steps.length - 1);
    const nextCode = steps[nextIdx]?.code ?? activeCode;

    if (policyIdArg && !hasNavigatedToId.current) {
      setParams({
        policyId: policyIdArg,
        id: policyIdArg,
        ...(quoteIdArg?.trim() ? { proposalNumber: quoteIdArg.trim() } : {}),
      });

      hasNavigatedToId.current = true;
      navigate({
        to: '/quotes/products/new/$id',
        params: { id: policyIdArg },
        search: { ...baseSearch, stepCode: nextCode },
        replace: true,
        viewTransition: false,
      }).catch(() => {});
    } else {
      goToStepCode(nextCode);
    }

    document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePreviousStep = () => {
    if (!steps.length) return;
    const idx = steps.findIndex(s => s.code === activeCode);
    if (idx <= 0) return;
    const prevCode = steps[idx - 1]?.code ?? activeCode;
    goToStepCode(prevCode);
    document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleResumeEditFromCalculation = () => {
    const firstCode = steps[0]?.code ?? '';
    if (firstCode) goToStepCode(firstCode);
  };

  // Navegação direta pelo número do step (só steps já preenchidos — o gate
  // fica no QuoteStepsNav via isStepFilled).
  const handleStepSelect = (index: number) => {
    const code = steps[index]?.code;
    if (!code) return;
    goToStepCode(code);
    document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return <NewQuoteFormSkeleton />;
  }

  const stepsUiIndex = activeIndex < 0 ? 0 : activeIndex;
  const isCalculationLayout = isLastStep;

  const renderActiveStep = () => {
    switch (activeCode) {
      case 'CLIENT_INFO':
        if (category === 'GROUP_LIFE_INSURANCE') {
          return (
            <div className='w-full bg-white rounded-2xl p-8 shadow-md'>
              <StepStipulator
                onNext={(policyIdArg, proposalNumberArg) =>
                  handleNextStep(policyIdArg, proposalNumberArg)
                }
                onBack={handlePreviousStep}
              />
            </div>
          );
        }
        return (
          <div className='w-full rounded-2xl bg-white p-4 shadow-md sm:p-8'>
            <StepInsuredData
              // @ts-ignore - Type incompatibility with composite forms
              form={mainForm}
              onNext={handleNextStep}
              onBack={handlePreviousStep}
              productId={productId || ''}
              productCode={productCode || ''}
              category={category || 'HOME_INSURANCE'}
              stepCode={activeCode}
            />
          </div>
        );
      case 'RISK_AUTO':
        return (
          <div className='w-full rounded-2xl bg-white p-4 shadow-md sm:p-8'>
            <StepPropertyData
              // @ts-ignore - Type incompatibility with composite forms
              form={mainForm}
              onNext={handleNextStep}
              onBack={handlePreviousStep}
              stepCode={activeCode}
              prefilledInsuredObjects={
                insuredObjectsState.length > 0 ? insuredObjectsState : undefined
              }
              onInsuredObjectsSave={setInsuredObjectsState}
            />
          </div>
        );
      case 'RISK_BUSINESS':
        return (
          <div className='w-full rounded-2xl bg-white p-4 shadow-md sm:p-8'>
            <StepRiskBusinessData
              // @ts-ignore - Type incompatibility with composite forms
              form={mainForm}
              onNext={handleNextStep}
              onBack={handlePreviousStep}
              stepCode={activeCode}
              initialRisks={initialBusinessRisks}
            />
          </div>
        );
      case 'PLAN':
        return (
          <div className='w-full bg-white rounded-2xl p-8 shadow-md'>
            <StepPlan
              onNext={() => handleNextStep()}
              onBack={handlePreviousStep}
            />
          </div>
        );
      case 'MEMBERS':
        return (
          <div className='w-full bg-white rounded-2xl p-8 shadow-md'>
            <StepRisk
              onNext={() => handleNextStep()}
              onBack={handlePreviousStep}
            />
          </div>
        );
      case 'COVERAGES':
        return (
          <div className='w-full rounded-2xl bg-white p-4 shadow-md sm:p-8'>
            <StepCoverages
              // @ts-ignore - Type incompatibility with composite forms
              form={mainForm}
              onNext={handleNextStep}
              onBack={handlePreviousStep}
              coverages={coveragesState}
              setCoverages={setCoveragesState}
              assistanceChoice={assistanceChoice}
              setAssistanceChoice={setAssistanceChoice}
              vrdTotal={vrdTotal > 0 ? vrdTotal : undefined}
              isViewMode={false}
              stepCode={activeCode}
            />
          </div>
        );
      case 'COMMERCIALIZATION':
        if (category === 'GROUP_LIFE_INSURANCE') {
          return (
            <div className='w-full bg-white rounded-2xl p-8 shadow-md'>
              <StepGroupLifeCommercialization
                onNext={() => handleNextStep()}
                onBack={handlePreviousStep}
              />
            </div>
          );
        }
        return (
          <div className='w-full rounded-2xl bg-white p-4 shadow-md sm:p-8'>
            <StepCommercialization
              // @ts-ignore - Type incompatibility with composite forms
              form={mainForm}
              onNext={handleNextStep}
              onBack={handlePreviousStep}
              productId={productId || ''}
              productCode={productCode || ''}
              stepCode={activeCode}
            />
          </div>
        );
      case 'CALCULATION':
        if (category === 'GROUP_LIFE_INSURANCE') {
          // Step 5 (Cálculo) é a própria tela de resumo: o prêmio já foi
          // calculado no "Calcular" do step Dados do risco (as vidas chegam
          // tarifadas da importação; lá roda só o roll-up policy-level).
          return (
            <div className='w-full bg-white rounded-2xl p-8 shadow-md'>
              <SummaryView
                onBack={handlePreviousStep}
                onExit={() => navigate({ to: '/quotes/products' })}
                isViewMode={isViewFlow}
              />
            </div>
          );
        }
        return (
          <div className='w-full rounded-2xl bg-white p-4 shadow-md sm:p-8'>
            <StepCalculation
              // @ts-ignore - Type incompatibility with composite forms
              form={mainForm}
              onBack={handlePreviousStep}
              isViewMode={false}
              onResumeEdit={handleResumeEditFromCalculation}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const body = (
    <div className='flex flex-col gap-8 lg:flex-row lg:gap-6 xl:gap-8'>
      <div
        className={`space-y-8 min-w-0 ${isCalculationLayout ? 'flex-[2]' : 'flex-1'}`}
      >
        {category === 'GROUP_LIFE_INSURANCE' ? (
          <GroupLifeQuoteStepsNav
            steps={steps}
            activeIndex={stepsUiIndex}
            onStepSelect={handleStepSelect}
          />
        ) : (
          <QuoteStepsNav
            steps={steps}
            activeIndex={stepsUiIndex}
            onStepSelect={handleStepSelect}
          />
        )}

        <mainForm.Subscribe selector={state => state.values.issuanceType}>
          {issuanceType => (
            <RequiredTooltipProvider
              restartKey={`${activeCode}:${String(issuanceType ?? '')}`}
            >
              {renderActiveStep()}
            </RequiredTooltipProvider>
          )}
        </mainForm.Subscribe>
      </div>

      {category !== 'BUSINESS_INSURANCE' &&
        category !== 'GROUP_LIFE_INSURANCE' && (
          <div className={isCalculationLayout ? 'lg:w-[320px] shrink-0' : ''}>
            <QuoteSummary />
          </div>
        )}
    </div>
  );

  if (category === 'GROUP_LIFE_INSURANCE') {
    return (
      <GroupLifeQuoteProvider>
        <GroupLifeHydrator>{body}</GroupLifeHydrator>
      </GroupLifeQuoteProvider>
    );
  }

  return body;
}

type QuoteStepsNavProps = {
  steps: QuoteSchema['steps'];
  activeIndex: number;
  onStepSelect: (index: number) => void;
  /** Quando presente, steps com dados já preenchidos aceitam navegação
   *  direta pelo clique no número; os demais seguem só o fluxo sequencial. */
  isStepFilled?: (stepCode: string) => boolean;
};

/** Stepper do wizard (mobile + desktop). Sem `isStepFilled`, é puramente
 *  visual, como sempre foi (navegação apenas sequencial pelos botões). */
function QuoteStepsNav({
  steps,
  activeIndex,
  onStepSelect,
  isStepFilled,
}: QuoteStepsNavProps) {
  const canJumpTo = (index: number) =>
    index !== activeIndex && Boolean(isStepFilled?.(steps[index]?.code ?? ''));

  return (
    <>
      <div className='md:hidden'>
        <div className='mb-3 text-sm font-medium text-alba-gray-600'>
          Etapa {activeIndex + 1} de {steps.length || 0}
        </div>
        <div className='overflow-hidden rounded-2xl bg-white px-3 py-5 shadow-md'>
          <div
            className='grid gap-1'
            style={{
              gridTemplateColumns: `repeat(${steps.length || 1}, minmax(0, 1fr))`,
            }}
          >
            {steps.map((step, index) => {
              const isActive = index === activeIndex;
              const isCompleted = index < activeIndex;
              const isNavigable = canJumpTo(index);

              return (
                <div
                  key={step.code || step.step}
                  className={[
                    'flex min-w-0 flex-col items-center gap-2',
                    isNavigable ? 'cursor-pointer' : '',
                  ].join(' ')}
                  onClick={isNavigable ? () => onStepSelect(index) : undefined}
                >
                  <div
                    className={[
                      'flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold',
                      isActive
                        ? 'bg-alba-blue-500 text-white'
                        : isCompleted
                          ? 'bg-alba-green-500 text-white'
                          : 'bg-alba-gray-200 text-alba-gray-600',
                    ].join(' ')}
                  >
                    {index + 1}
                  </div>
                  <span
                    className={[
                      'line-clamp-2 min-w-0 text-center text-[10px] leading-tight',
                      isActive
                        ? 'text-alba-black-500 font-medium'
                        : 'text-alba-gray-600',
                    ].join(' ')}
                    title={step.stepName}
                  >
                    {step.stepName}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className='hidden md:block'>
        <Steps.Root
          step={activeIndex}
          count={steps.length || 0}
          linear={!isStepFilled}
          onStepChange={
            isStepFilled
              ? event => {
                  if (canJumpTo(event.step)) onStepSelect(event.step);
                }
              : undefined
          }
        >
          <Steps.List>
            {steps.map((step, index) => (
              <Steps.Item key={step.code || step.step} index={index}>
                <Steps.Trigger
                  className={
                    canJumpTo(index)
                      ? 'cursor-pointer'
                      : 'pointer-events-none cursor-default'
                  }
                >
                  <Steps.Indicator />
                  <Steps.Title>{step.stepName}</Steps.Title>
                </Steps.Trigger>
                {index < steps.length - 1 && <Steps.Separator />}
              </Steps.Item>
            ))}
          </Steps.List>
        </Steps.Root>
      </div>
    </>
  );
}

/** Variante PME: precisa viver dentro do `GroupLifeQuoteProvider` para ler
 *  o contexto/snapshot e decidir quais steps já têm dados. */
function GroupLifeQuoteStepsNav(
  props: Omit<QuoteStepsNavProps, 'isStepFilled'>
) {
  const isStepFilled = useGroupLifeStepFilled();
  const { data: quoteDetails, isLoading: isLoadingQuoteDetails } =
    useGroupLifeQuoteDetails();
  // Resumo de uma VERSÃO da cotação: retrato histórico, só consulta.
  const isVersionSnapshot = Boolean(useQuotationVersionId());

  // Cotação encerrada, expirada, excluída ou recusada não é mais editável: a
  // trilha deixa de aceitar clique, senão ela seria a porta de entrada para os
  // steps de preenchimento de uma cotação que não vale mais.
  //
  // Enquanto o status não chegou, a trilha também não clica: o snapshot é
  // buscado a cada mount e liberar a navegação no primeiro paint deixava a
  // trilha clicável por um instante antes de travar.
  if (
    isLoadingQuoteDetails ||
    isVersionSnapshot ||
    isTerminalPortalStatus(quoteDetails?.statusPortal)
  ) {
    return <QuoteStepsNav {...props} isStepFilled={() => false} />;
  }

  return <QuoteStepsNav {...props} isStepFilled={isStepFilled} />;
}

/** Dispara o hook de hidratação dentro do `GroupLifeQuoteProvider`.
 *  Quando o usuário recarrega a página com `policyId` na URL, o contexto
 *  é populado a partir do snapshot no InsureMO. */
function GroupLifeHydrator({ children }: { children: React.ReactNode }) {
  const { isHydrating, isEditFlow } = useHydrateGroupLifeQuote();
  // Na primeira hidratação de uma EDIÇÃO, mostra o skeleton em vez dos campos
  // vazios "piscando" preenchidos quando o snapshot chega. Re-navegações entre
  // steps já têm o snapshot em cache → `isHydrating` é false e não bloqueia.
  if (isEditFlow && isHydrating) {
    return <NewQuoteFormSkeleton />;
  }
  return <>{children}</>;
}
